import * as React from 'react';

import { cn } from '../../lib/utils';

type StickyHeaderMode = 'off' | 'page' | 'contained';

const StickyHeaderContext = React.createContext<StickyHeaderMode>('off');

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
    stickyHeader?: boolean;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
    ({ className, stickyHeader = false, ...props }, forwardedRef) => {
        const wrapperRef = React.useRef<HTMLDivElement | null>(null);
        const tableRef = React.useRef<HTMLTableElement | null>(null);
        const [overflowing, setOverflowing] = React.useState(false);

        const setTableRef = React.useCallback(
            (node: HTMLTableElement | null) => {
                tableRef.current = node;
                if (typeof forwardedRef === 'function') {
                    forwardedRef(node);
                } else if (forwardedRef) {
                    (forwardedRef as React.MutableRefObject<HTMLTableElement | null>).current =
                        node;
                }
            },
            [forwardedRef],
        );

        React.useLayoutEffect(() => {
            if (!stickyHeader) return;

            const wrapper = wrapperRef.current;
            const table = tableRef.current;
            if (!wrapper || !table) return;

            const measure = () => setOverflowing(table.offsetWidth > wrapper.clientWidth + 1);
            measure();

            if (typeof ResizeObserver === 'undefined') return;

            let frame: number | undefined;
            const observer = new ResizeObserver(() => {
                if (frame !== undefined) cancelAnimationFrame(frame);
                frame = requestAnimationFrame(measure);
            });
            observer.observe(wrapper);
            observer.observe(table);

            return () => {
                if (frame !== undefined) cancelAnimationFrame(frame);
                observer.disconnect();
            };
        }, [stickyHeader]);

        const mode: StickyHeaderMode = !stickyHeader ? 'off' : overflowing ? 'contained' : 'page';

        return (
            <div
                ref={wrapperRef}
                className={cn(
                    'relative w-full pb-0.5 pt-0',
                    mode === 'off' && 'overflow-auto',
                    mode === 'page' && 'overflow-visible',
                    mode === 'contained' &&
                        'overflow-auto max-h-[calc(100dvh-var(--app-bar-height)-1rem)]',
                )}
                data-sticky-header={stickyHeader ? mode : undefined}
            >
                <StickyHeaderContext.Provider value={mode}>
                    <table
                        ref={setTableRef}
                        className={cn(
                            'w-full caption-bottom text-sm tabular-nums border-collapse',
                            className,
                        )}
                        {...props}
                    />
                </StickyHeaderContext.Provider>
            </div>
        );
    },
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
    const mode = React.useContext(StickyHeaderContext);

    return (
        <thead
            ref={ref}
            className={cn(
                '[&_tr]:border-b [&_tr]:border-border sticky z-10 bg-[hsl(240_5%_96.7%)]',
                mode === 'page' ? 'top-[var(--app-bar-height)]' : 'top-0',
                mode !== 'off' && '[&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]',
                className,
            )}
            {...props}
        />
    );
});
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
    <tbody
        ref={ref}
        className={cn(
            '[&_tr:last-child]:border-0 [&_tr:nth-child(even)>td]:bg-muted/20 [&_tr:hover>td]:bg-muted/40 [&_tr[data-state=selected]>td]:bg-muted [&_tr.cursor-pointer:hover>td]:bg-muted/80 [&_tr.cursor-pointer:hover:active>td]:bg-muted',
            className,
        )}
        {...props}
    />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
    <tfoot
        ref={ref}
        className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
        {...props}
    />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
    ({ className, ...props }, ref) => (
        <tr
            ref={ref}
            className={cn(
                'border-b border-border/70 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary-500',
                className,
            )}
            {...props}
        />
    ),
);
TableRow.displayName = 'TableRow';

/** Makes a whole row open its detail with mouse or keyboard (Enter/Space), leaving nested controls alone. */
function getClickableRowProps(onActivate: () => void) {
    return {
        tabIndex: 0,
        onClick: onActivate,
        onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
            if (event.target !== event.currentTarget) {
                return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onActivate();
            }
        },
    };
}

const TableHead = React.forwardRef<
    HTMLTableCellElement,
    React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
    <th
        ref={ref}
        className={cn(
            'h-11 px-3 py-2.5 text-left align-middle text-sm font-medium text-muted-foreground first:pl-4 last:pr-4 sm:first:pl-5 sm:last:pr-5 [&:has([role=checkbox])]:w-12 [&:has([role=checkbox])]:px-2 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
            className,
        )}
        {...props}
    />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
    HTMLTableCellElement,
    React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
    <td
        ref={ref}
        className={cn(
            'transition-colors px-3 py-3 align-middle first:pl-4 last:pr-4 sm:first:pl-5 sm:last:pr-5 [&:has([role=checkbox])]:w-12 [&:has([role=checkbox])]:px-2 [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
            className,
        )}
        {...props}
    />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
    HTMLTableCaptionElement,
    React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
    <caption ref={ref} className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />
));
TableCaption.displayName = 'TableCaption';

export { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, getClickableRowProps };
